import type { LAYER_CONFIG } from './SurveyLayerConfig';
import NumberUtils from 'js/helpers/NumberUtils';
import type { LanguageFile } from 'js/helpers/LanguageUtils';

import {
  SURVEY_LAYER_CATEGORIES,
  SURVEY_LAYER_VALUES,
  SURVEY_LAYERS,
  type SurveyLayer,
} from 'js/constants/SurveyLayers';

import { getSourceCategory } from '../../helpers/SurveyUtils';
import { getImageUrl } from './SurveyImageMapping';
import {
  getActiveSurveyCountryCode,
  getSurveyLayerDataRange,
  isSoilClassLayer,
} from './country/SurveyCountryStrategy';

export const getSurveyConfig = (layer: SurveyLayer, countryCode?: string): LAYER_CONFIG => {
  if (!SURVEY_LAYER_VALUES.includes(layer)) {
    return null;
  }

  // Some layers have key !== value (e.g. OM → 'ORGANIC_MATTER', CEC → 'CATION_EXCHANGE_CAPACITY').
  // Language files are keyed by the SURVEY_LAYERS key (short name), so we reverse-map here.
  const langKey = Object.keys(SURVEY_LAYERS).find((k) => SURVEY_LAYERS[k] === layer) || layer;

  // VALUE MAPPING
  const interval = getSurveyLayerDataRange(layer, countryCode || getActiveSurveyCountryCode());
  const minValue = interval ? interval[0] : 0.0;
  const maxValue = interval ? interval[1] : 1.0;

  const mapValue = (val, outMin, outMax) => {
    return NumberUtils.limitMap(val, minValue, maxValue, outMin, outMax);
  };

  const reverseMapValue = (val, inMin, inMax) => {
    return NumberUtils.limitMap(val, inMin, inMax, minValue, maxValue);
  };

  // THE REST
  const category = getSourceCategory(layer);

  let unit = category === SURVEY_LAYER_CATEGORIES.TEXTURE ? '%' : null;
  if (isSoilClassLayer(layer)) {
    unit = null;
  }
  if (layer === SURVEY_LAYERS.ALTITUDE) {
    unit = 'm';
  }
  if (layer === SURVEY_LAYERS.CEC) {
    unit = 'cmol/kg';
  }

  return {
    minValue: minValue,
    maxValue: maxValue,
    category: category,
    unit: unit,
    getName: (LangFile: LanguageFile) => LangFile.SurveyLayers[langKey].name,
    getShorthand: (LangFile: LanguageFile) => LangFile.SurveyLayers[langKey].shorthand,
    mapValue: mapValue,
    reverseMapValue: reverseMapValue,
    getImageUrl: (
      images,
      classificationsEnabled: boolean,
      variationsEnabled: boolean,
      viewMode: String
    ): string => {
      return getImageUrl(images, layer, classificationsEnabled, variationsEnabled, viewMode);
    },
  };
};
